// state 定义 + mutations

import { DEFAULT_DAMAGE_INSURANCE_BRIEF } from '@g/utils/damageInsurance'
// brand-fence:start id=kkren-create-tax-whld-state-import brand=kkren predicate=data-driven
import { DEFAULT_TAX_WHLD_METHOD } from '@g/order-shared/taxWhldMethod'
// brand-fence:end id=kkren-create-tax-whld-state-import

export function createInitialSplitState () {
  return {
    active: false,
    loading: false,
    mode: 'sea',
    decision: '',
    dialogOpen: false,
    pendingMode: '',
    pendingSrid: '',
    allPclids: [],
    furniturePclids: [],
    otherPclids: [],
    secondPclids: [],
    keepOnlyPclids: [],
    allModel: null,
    otherModel: null,
  }
}

export function createInitialViewState () {
  return {
    deliveryMode: '0',
    pendingTransport: '',
    estimateWeight: '',
    useFinalDiscountRate: true,
    damageInsuranceSelections: {},
    damageInsuranceBrief: DEFAULT_DAMAGE_INSURANCE_BRIEF,
    damageInsuranceError: '',
    confirmations: {
      damageInsuranceRisk: false,
      hzkPickup: false,
      x3OverTen: false,
    },
    confirmationErrors: {
      damageInsuranceRisk: false,
      damageInsuranceBrief: false,
      hzkPickup: false,
      x3OverTen: false,
    },
    // 混合拆单（家具走海運、例外件另建海快单或留倉）的会话状态
    split: createInitialSplitState(),
    // 海快家具规则确认单已确认的规则 key，规则变化后需重新确认
    seFurnitureAckKey: '',
    // 当前下单就是某笔拆单第一单的待建第二单时的本地记录
    splitSecondRecord: null,
  }
}

// 模块初始 state 工厂：正式 store 用一份，拆单第二单预估的临时 store 另建一份隔离。
export function createInitialModuleState () {
  return {
    createForm: null,
    createPrepare: null,
    createView: createInitialViewState(),
    couponUsableList: [],
    currAddrInfo: null,
    currTradevanAddr: null,
    isAutoCreate: false,
    declCargoesByFeeType: {},
    declCargoesLoadingKey: '',
  }
}

export const state = createInitialModuleState()

function sumDeclCargoesTotalAmount (list) {
  return list.reduce((prev, curr) => {
    return prev + Number(curr.totalAmount || 0)
  }, 0)
}

export function normalizeDeclCargoesCache ({ list, error }) {
  const declCargoes = Array.isArray(list) ? list : []
  if (error) {
    return {
      list: declCargoes,
      amount: 0,
      ready: false,
      error,
    }
  }
  return {
    list: declCargoes,
    amount: Math.round(sumDeclCargoesTotalAmount(declCargoes)),
    ready: true,
    error: '',
  }
}

export const mutations = {
  resetCreateViewState (state) {
    state.createView = createInitialViewState()
  },
  setCreateForm (state, createForm) {
    state.createForm = createForm
  },
  setTradevanAddrid (state, tradevanAddrid) {
    if (!state.createForm) return
    state.createForm.tradevanAddrid = tradevanAddrid
  },
  setSrid (state, srid) {
    if (!state.createForm) return
    state.createForm.srid = srid
  },
  setCneeAddrid (state, cneeAddrid) {
    if (!state.createForm) return
    state.createForm.cneeAddrid = cneeAddrid
  },
  setJyoCarrier (state, jyoCarrier) {
    if (!state.createForm) return
    state.createForm.jyoCarrier = jyoCarrier
  },
  setTimeInsuranceSelected (state, value) {
    if (!state.createForm) return
    state.createForm.jyoExtraInfo.isTimeIns = value
  },
  setLossInsuranceAmount (state, amount) {
    if (!state.createForm) return
    state.createForm.jyoExtraInfo.lostInsAmt = amount
  },
  setCreateServiceOption (state, { key, value }) {
    if (!state.createForm || !['isFs', 'isWb', 'isCtn'].includes(key)) return
    state.createForm[key] = value
  },
  setCreatePrepare (state, createPrepare) {
    state.createPrepare = createPrepare
    const split = state.createView?.split
    // 拆单海快模式下主模型就是全部包裹，刷新后同步给拆单快照。
    if (split?.active && split.mode === 'se' && createPrepare) {
      split.allModel = createPrepare
    }
  },
  setCreateSplit (state, patch) {
    state.createView.split = { ...state.createView.split, ...(patch || {}) }
  },
  resetCreateSplit (state) {
    state.createView.split = createInitialSplitState()
  },
  requestSplitMode (state, { mode, srid }) {
    state.createView.split = { ...state.createView.split, pendingMode: mode, pendingSrid: srid || '' }
  },
  setSplitDecision (state, decision) {
    state.createView.split = { ...state.createView.split, decision: decision || '' }
  },
  dropSplitPclids (state, pclids) {
    const removed = (Array.isArray(pclids) ? pclids : [pclids]).map(String)
    const split = state.createView.split
    if (!split?.active) return
    const keep = list => (list || []).filter(pclid => !removed.includes(String(pclid)))
    const next = {
      ...split,
      allPclids: keep(split.allPclids),
      furniturePclids: keep(split.furniturePclids),
      otherPclids: keep(split.otherPclids),
      secondPclids: keep(split.secondPclids),
      keepOnlyPclids: keep(split.keepOnlyPclids),
    }
    // 家具或例外件被移空后已不再是混合勾选，回到普通下单流程。
    if (!next.furniturePclids.length || !next.otherPclids.length) {
      state.createView.split = createInitialSplitState()
      return
    }
    if (!next.secondPclids.length) next.decision = ''
    state.createView.split = next
  },
  setSeFurnitureAckKey (state, key) {
    state.createView.seFurnitureAckKey = key || ''
  },
  setSplitSecondRecord (state, record) {
    state.createView.splitSecondRecord = record || null
  },
  setCouponUsableList (state, couponUsableList) {
    state.couponUsableList = couponUsableList
  },
  setCouponCrid (state, crid) {
    if (!state.createForm) return
    state.createForm.crid = crid
  },
  setCurrency (state, currency) {
    if (!state.createForm) return
    state.createForm.currency = currency
  },
  setApartPclids (state, apartPclids) {
    if (!state.createForm) return
    state.createForm.jyoExtraInfo.apartPclids = apartPclids
  },
  setIsAutoCreate (state, isAutoCreate) {
    state.isAutoCreate = isAutoCreate
  },
  resetDeclCargoesByFeeType (state) {
    state.declCargoesByFeeType = {}
  },
  setDeclCargoesLoadingKey (state, loadingKey) {
    state.declCargoesLoadingKey = loadingKey
  },
  setDeclCargoesByFeeType (state, payload) {
    if (payload.loadingKey && state.declCargoesLoadingKey && payload.loadingKey !== state.declCargoesLoadingKey) {
      return
    }
    state.declCargoesByFeeType = {
      ...state.declCargoesByFeeType,
      [payload.type]: normalizeDeclCargoesCache(payload),
    }
  },
  setCurrAddrInfo (state, currAddrInfo) {
    if (currAddrInfo?.addrid) {
      try {
        localStorage.setItem(
          'jyorderCreateLastAddr',
          JSON.stringify({
            addrid: currAddrInfo.addrid,
            city: currAddrInfo.city,
          })
        )
      } catch (e) {
        console.error(e)
      }
    }
    state.currAddrInfo = currAddrInfo
  },
  setCurrTradevanAddr (state, currTradevanAddr) {
    if (currTradevanAddr?.addrid) {
      try {
        localStorage.setItem(
          'jyorderCreateLastTradevanAddr',
          JSON.stringify({
            addrid: currTradevanAddr.addrid,
            consignee: currTradevanAddr.consignee,
            idNum: currTradevanAddr.idNum,
            mobile: currTradevanAddr.mobile,
          })
        )
      } catch (e) {
        console.error(e)
      }
    }
    state.currTradevanAddr = currTradevanAddr
  },
  initCreateForm (state, form) {
    state.createView = createInitialViewState()
    state.couponUsableList = []
    state.currAddrInfo = null
    state.currTradevanAddr = null
    state.createForm = {
      pclids: form.pclids,
      currency: '',
      isTwt: '',
      srid: '',
      crid: '', // 優惠券
      cneeAddrid: '',
      tradevanAddrid: '',
      jyoCarrier: '',
      isFs: 0,
      isCtn: 0, // 增加紙箱包裝
      isWs: 0,
      isWb: 0,
      isWx: 0,
      isFcc: 0,
      isOwsu: 0,
      jyoShpMethod: '1',
      useFeeType: 'frt', // notIncTax | frt | tax | busi
      // brand-fence:start id=kkren-create-tax-whld-state-default brand=kkren predicate=data-driven
      jyoExtraFees: {
        taxWhldMethod: DEFAULT_TAX_WHLD_METHOD,
      },
      // brand-fence:end id=kkren-create-tax-whld-state-default
      jyoExtraInfo: {
        apartPclids: [],
        declCargoes: [],
        srInclTax: '0',
        srInclFreq: '0',
        isTimeIns: '0',
        lostInsAmt: 0,
        lostInsRate: 1,
        cargoesValRatio: '0.2', // 0.5 || 1
      },
    }
  },
}
